import {
  NextButton,
  NextButtonIcon,
  NextStatusBadge,
  type NextStatusBadgeProps,
  Spinner,
} from '@cint/wonderment';
import { ReactNode, useState } from 'react';

import { TrackingMethodCard } from '@/components';
import { DeleteDialog } from '@/components/DeleteDialog';

export type ConnectionStatus =
  | 'notConnected'
  | 'connected'
  | 'expiringSoon'
  | 'expired'
  | 'error';

/** Statuses that still send events, so they read as connected everywhere else. */
export const isActiveConnectionStatus = (status: ConnectionStatus) =>
  status === 'connected' || status === 'expiringSoon';

export type ConnectionScope = 'company' | 'panel';

export const STATUS_BADGE: Record<
  ConnectionStatus,
  { color: NextStatusBadgeProps['color']; label: string }
> = {
  notConnected: { color: 'gray', label: 'Not connected' },
  connected: { color: 'success', label: 'Connected' },
  expiringSoon: { color: 'warning', label: 'Expiring soon' },
  expired: { color: 'danger', label: 'Error' },
  error: { color: 'danger', label: 'Error' },
};

// Nothing gates this card on the panel fetch, so the copy has to name the
// panel before its name arrives.
const PANEL_SCOPE_FALLBACK = 'this panel';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How the "expiring soon" copy counts down to `expires_at`. Rounded up, so a
 * token with any time left on it never reads as expiring today, and floored at
 * 0 for the window between the token lapsing and the API catching up.
 */
const getDaysUntilExpiry = (expiresAt?: string | null): number | undefined => {
  if (!expiresAt) return undefined;

  const expiryTime = new Date(expiresAt).getTime();
  if (Number.isNaN(expiryTime)) return undefined;

  return Math.max(0, Math.ceil((expiryTime - Date.now()) / MS_PER_DAY));
};

/**
 * The lead time in the "expiring soon" copy. Without a usable `expires_at` the
 * card still has to say something, so it drops the countdown rather than the
 * whole warning.
 */
const getExpiryPhrase = (expiresAt?: string | null): string => {
  const days = getDaysUntilExpiry(expiresAt);

  if (days === undefined) return 'soon';
  if (days === 0) return 'today';
  return days === 1 ? 'in 1 day' : `in ${days} days`;
};

const getStatusDescription = (
  status: ConnectionStatus,
  title: string,
  providerName: string,
  scope: ConnectionScope,
  scopeName?: string,
  expiresAt?: string | null
): string =>
  ({
    connected: `Actively sending conversion events to ${title}. `,
    expiringSoon: `${providerName} access token expires ${getExpiryPhrase(
      expiresAt
    )}. Update it now to keep conversion tracking from failing.`,
    expired: `${providerName} access token has expired. Reconnect your ${providerName} account to resume tracking.`,
    notConnected:
      scope === 'panel'
        ? `Sends conversion events to ${title}. Once connected, it's ready to use on ${
            scopeName ?? PANEL_SCOPE_FALLBACK
          } only, connect separately for other panels.`
        : `Sends conversion events to ${providerName}. Once connected, it's ready to use on every panel, you'll still select it per campaign. Connection will need to be updated every 60 days.`,
    // Used when the connection is in error but the API sent no error_details.
    error: `We lost access to your ${providerName} account. This usually happens if your password changed or access was turned off.`,
  })[status];

const getScopeLabel = (scope: ConnectionScope, scopeName?: string) => {
  const level = scope === 'panel' ? 'Panel-level' : 'Company-level';

  return scopeName ? `${level} \u00b7 Scoped to ${scopeName}` : level;
};

const getDisconnectCopy = (
  scope: ConnectionScope,
  title: string,
  providerName: string,
  scopeName?: string
) =>
  scope === 'panel'
    ? {
        title: `Disconnect ${title} for ${scopeName ?? PANEL_SCOPE_FALLBACK}`,
        description: `After Disconnection, the integration will be lost for ${
          scopeName ?? PANEL_SCOPE_FALLBACK
        }. Any campaigns will continue to function, but tracking data will not be sent to ${providerName}.`,
      }
    : {
        title: `Disconnect ${providerName} for all panels`,
        description: (
          <>
            After Disconnection, the integration will be lost for <b>all</b>{' '}
            panels. Campaigns keep running, and cost recording for reporting
            still works - only the signal sent to {providerName} stops.
          </>
        ),
      };

export interface ConnectionStatusCardProps {
  icon: ReactNode;
  title: string;
  /** The platform alone ("Meta"), where `title` is the full "Meta Ads". */
  providerName: string;
  scope: ConnectionScope;
  scopeName?: string;
  status: ConnectionStatus;
  /** The connection's `expires_at`, counted down in the "expiring soon" copy. */
  expiresAt?: string | null;
  /** Overrides the stock copy for the status, e.g. the API's error_details. */
  description?: ReactNode;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onViewInfo?: () => void;
  onReconnect?: () => void;
  isActionDisabled?: boolean;
  isActionLoading?: boolean;
  isLoading?: boolean;
  className?: string;
}

export const ConnectionStatusCard = ({
  icon,
  title,
  providerName,
  scope,
  scopeName,
  status,
  expiresAt,
  description,
  onConnect,
  onDisconnect,
  onViewInfo,
  onReconnect,
  isActionDisabled = false,
  isActionLoading = false,
  isLoading = false,
  className,
}: ConnectionStatusCardProps) => {
  const badge = STATUS_BADGE[status];
  const disconnectCopy = getDisconnectCopy(
    scope,
    title,
    providerName,
    scopeName
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Expiring soon leads with Disconnect and calls the reconnect "Update token",
  // since nothing is broken yet — it's the token that needs refreshing.
  const isExpiringSoon = status === 'expiringSoon';

  const reconnectButton = (
    <NextButton
      variant="primary"
      size="base"
      text={isExpiringSoon ? 'Update token' : `Reconnect ${providerName}`}
      onClick={onReconnect}
      isDisabled={isActionDisabled}
      isLoading={isActionLoading}
    />
  );

  const disconnectButton = (
    <NextButton
      variant="outline"
      size="base"
      text="Disconnect"
      onClick={() => setIsDeleteDialogOpen(true)}
      isDisabled={isActionDisabled}
    />
  );

  return (
    <>
      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        title={disconnectCopy.title}
        primaryButtonText="Disconnect"
        isLoading={isActionLoading}
        description={disconnectCopy.description}
        onDelete={() => {
          onDisconnect?.();
          setIsDeleteDialogOpen(false);
        }}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />
      <TrackingMethodCard
        icon={icon}
        title={title}
        subtitle={getScopeLabel(scope, scopeName)}
        description={
          isLoading
            ? `Checking your ${providerName} connection status…`
            : description ??
              getStatusDescription(
                status,
                title,
                providerName,
                scope,
                scopeName,
                expiresAt
              )
        }
        className={className}
        // The expiring-soon warning runs long, so its actions get their own
        // row rather than squeezing the copy against them.
        actionsPlacement={isExpiringSoon ? 'below' : 'inline'}
        badge={
          isLoading ? (
            <Spinner size="sm" />
          ) : (
            <NextStatusBadge color={badge.color} label={badge.label} withDot />
          )
        }
        actions={
          isLoading ? undefined : (
            <>
              {status === 'notConnected' && (
                <NextButton
                  variant="primary"
                  size="base"
                  text="Connect"
                  onClick={onConnect}
                  isDisabled={isActionDisabled}
                  isLoading={isActionLoading}
                />
              )}
              {/* Expiring soon still works, so it keeps Disconnect — and puts
                  it first, ahead of the token refresh. */}
              {isExpiringSoon && (
                <>
                  {disconnectButton}
                  {reconnectButton}
                </>
              )}
              {(status === 'error' || status === 'expired') && reconnectButton}
              {status === 'connected' && disconnectButton}
              {status !== 'notConnected' && onViewInfo && (
                <NextButtonIcon
                  iconName="Info"
                  alt="View connection details"
                  onClick={onViewInfo}
                />
              )}
            </>
          )
        }
      />
    </>
  );
};
